import {
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material"
import { createUseStyles, useTheme } from "react-jss"
import { IRawDataQuality } from "../../util/interface"
import { useAppDispatch, useAppSelector } from "../../util/hooks"
import {
  rawDataQualityDataSelector,
  rowSlection,
  toggleRawDataDetails,
} from "./DataMetaDataSlice"
import styled from "@emotion/styled"
import React from "react"
import { v4 as uuidv4 } from "uuid"
const useStyles = createUseStyles({
  tableContainer: {
    textAlign: "center",
    borderRadius: "5px",
  },
  table: {},
  root: {
    "& .MuiTableCell-head": {
      backgroundColor: "#fbfbfb",
    },
  },
  tableHead: {
    backgroundColor: "#f5f5f5",
  },
  headerColor: {
    color: "#7D7D7D",
    fontFeatureSettings: "'clig' off, 'liga' off",
    fontFamily: ({ theme }: any) => theme.primaryFontFamily,
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "14px", // 127.273%
  },
  tableCell: {
    fontSize: "14px",
    borderRight: "1px solid #e0e0e0",
    "&:first-child": {
      borderTopLeftRadius: "4px",
      borderRight: "none",
    },
    "&:last-child": {
      borderTopRightRadius: "4px",
      borderRight: "none",
    },
  },
  rowDataNameCell: {
    color: "#7D7D7D !important",
    fontFeatureSettings: "'clig' off, 'liga' off !important",
    fontFamily: ({ theme }: any) => theme.primaryFontFamily + " !important",
    fontSize: "16px !important",
    fontStyle: "normal !important",
    fontWeight: "700 !important",
    lineHeight: "14px !important",
  },
  schemeCheckCell: {
    display: "flex",
    width: "106px",
    padding: "3px 12px",
    justifyContent: "center",
    alignItems: "center",
    gap: "2px",
    borderRadius: "15px",
    background: "#D0FAE5",
  },
  bars: {
    display: "flex",
    gap: 5,
  },
  pass: {
    width: 80,
    background: "#27AF8D",
    height: 4,
  },
  fail: {
    width: 80,
    background: "#EAEAEA",
    height: 4,
  },
  label: {
    color: "#171717",
    fontFamily: ({ theme }: any) => theme.primaryFontFamily,
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
    letterSpacing: "0.28px",
    textAlign: "center",
  },
  barWrapper: {
    width: "fit-content",
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  processedRowsPercentage: {
    display: "inline-flex",
    padding: "1px 6px",
    justifyContent: "center",
    alignItems: "center",
    gap: "7px",
    background: "#CFFFE1",
  },
  processedRowsPercentageText: {
    color: "#30D468",
    fontFamily: ({ theme }: any) => theme.primaryFontFamily,
    fontSize: "10px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
    letterSpacing: "0.2px",
  },
  processedRowsWrapper: {
    display: "flex",
    gap: "10px",
  },
  qc: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
  },
  commonHeader: {
    justifySelf: "center",
    fontSize: 14,
  },
  greenText: {
    color: "#27af8d",
  },
  redText: {
    color: "#E21824",
  },
  yellowText: {
    color: "black",
  },
  qcWrapper: {
    display: "grid",
    rowGap: 10,
    textAlign: "center",
  },
})

const ResultDiv = styled.div`
  height: 25px;
  width: 55px;
  display: inline-block;
  padding: 2px 10px;
  border-radius: 5px;
  margin: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  margin-right: auto;
`

const PassDiv = styled(ResultDiv)`
  background-color: #27af8d;
  color: white;
`

const FailedDiv = styled(ResultDiv)`
  background-color: #e21824;
  color: white;
`

export const RawDataTable = () => {
  const theme: any = useTheme()
  const classes = useStyles({ theme })
  const data: IRawDataQuality[] = useAppSelector(rawDataQualityDataSelector) // Get data from Redux
  const selectedRow = useAppSelector(
    (state) => state.dataMetaData.selectedRawDataRow,
  ) // Get selected row
  const dispatch = useAppDispatch() // Get dispatch function

  const isSelected = (rowName: string) => selectedRow === rowName // Check if row is selected
  const [page, setPage] = React.useState(0) // Current page state
  const [rowsPerPage, setRowsPerPage] = React.useState(10) // Rows per page state

  // Handle row selection
  const handleClick = (event: any, rowName: string) => {
    dispatch(rowSlection(event.target["checked"] ? rowName : "")) // Select or deselect row
  }

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage)
  }

  // Handle change in rows per page
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value) // Update rows per page
    setPage(0) // Reset to first page
  }

  return (
    <Paper>
      <TableContainer
        sx={{ maxHeight: 515 }}
        className={classes.tableContainer}
      >
        <Table stickyHeader aria-label="sticky table" className={classes.table}>
          <TableHead className={classes.root}>
            <TableRow>
              <TableCell
                padding="checkbox"
                className={classes.tableCell}
              ></TableCell>
              <TableCell
                align={"left"}
                sx={{ minWidth: 200 }}
                className={classes.tableCell}
              >
                <Typography variant="tableHeader">Raw Data Name</Typography>
              </TableCell>
              <TableCell
                align={"center"}
                sx={{ minWidth: 230 }}
                className={classes.tableCell}
              >
                <Typography variant="tableHeader">Number of Rows</Typography>
              </TableCell>
              <TableCell
                align={"center"}
                sx={{ minWidth: 450 }}
                className={classes.tableCell}
              >
                <div className={classes.qcWrapper}>
                  <Typography variant="tableHeader">Quality Check</Typography>
                  <div className={classes.qc}>
                    <Typography variant="tableHeader">Schema</Typography>
                    <Typography variant="tableHeader">Unique</Typography>
                    <Typography variant="tableHeader">Not Null</Typography>
                  </div>
                </div>
              </TableCell>
              <TableCell
                align={"center"}
                sx={{ minWidth: 330 }}
                className={classes.tableCell}
              >
                <Typography variant="tableHeader">
                  Overall Quality Check
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.length
              ? data
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) // Paginate data
                  .map((row: IRawDataQuality) => {
                    const isItemSelected = isSelected(row.raw_data_name) // Check if current row is selected
                    return (
                      <TableRow
                        key={uuidv4()}
                        sx={{ cursor: "pointer" }}
                        selected={isItemSelected}
                      >
                        <TableCell className="checkbox">
                          <Checkbox
                            color="primary"
                            inputProps={{ "aria-labelledby": "asddk" }}
                            role="checkbox"
                            tabIndex={-1}
                            checked={isItemSelected} // Checkbox state
                            aria-checked={isItemSelected}
                            onClick={(e) => handleClick(e, row.raw_data_name)} // Handle checkbox click
                          />
                        </TableCell>
                        <TableCell
                          align={"left"}
                          className={`${classes.tableCell} ${classes.rowDataNameCell}`}
                          onClick={() => {
                            dispatch(
                              toggleRawDataDetails({
                                isRdqDetailsTableActive: true,
                                selectedRawData: row.raw_data_name,
                              }),
                            ) // Show details
                            handleClick(
                              { target: { checked: true } },
                              row.raw_data_name,
                            )
                          }}
                        >
                          {row.raw_data_name}
                        </TableCell>
                        <TableCell
                          align={"center"}
                          className={classes.tableCell}
                          onClick={() => {
                            dispatch(
                              toggleRawDataDetails({
                                isRdqDetailsTableActive: true,
                                selectedRawData: row.raw_data_name,
                              }),
                            ) // Show details
                            handleClick(
                              { target: { checked: true } },
                              row.raw_data_name,
                            )
                          }}
                        >
                          {row.row_processed}
                        </TableCell>
                        <TableCell
                          align={"center"}
                          className={classes.tableCell}
                          onClick={() => {
                            dispatch(
                              toggleRawDataDetails({
                                isRdqDetailsTableActive: true,
                                selectedRawData: row.raw_data_name,
                              }),
                            ) // Show details
                            handleClick(
                              { target: { checked: true } },
                              row.raw_data_name,
                            )
                          }}
                        >
                          <div className={classes.qc}>
                            <div className={classes.commonHeader}>
                              {getQualityCheckStatus(row.schema_check, classes)}
                            </div>
                            <div className={classes.commonHeader}>
                              {getQualityCheckStatus(
                                row.quality_check_unique,
                                classes,
                              )}
                            </div>
                            <div className={classes.commonHeader}>
                              {getQualityCheckStatus(
                                row.quality_check_null,
                                classes,
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell
                          align={"center"}
                          onClick={() => {
                            dispatch(
                              toggleRawDataDetails({
                                isRdqDetailsTableActive: true,
                                selectedRawData: row.raw_data_name,
                              }),
                            ) // Show details
                            handleClick(
                              { target: { checked: true } },
                              row.raw_data_name,
                            )
                          }}
                        >
                          {getQualityStatus(row.quality_check_null)}
                        </TableCell>
                      </TableRow>
                    )
                  })
              : ""}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 20]}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage} // Handle page change
        onRowsPerPageChange={handleChangeRowsPerPage} // Handle rows per page change
      />
    </Paper>
  )
}

const getQualityCheckStatus = (status: string, classes: any) => {
  if (status === "pass") {
    return <span className={classes.greenText}>Pass</span>
  } else if (status === "fail") {
    return <span className={classes.redText}>Fail</span>
  } else {
    return <span className={classes.yellowText}>{status}</span>
  }
}

const getQualityStatus = (status: string) => {
  if (status === "pass") {
    return <PassDiv>Pass</PassDiv>
  } else if (status === "fail") {
    return <FailedDiv>Fail</FailedDiv>
  } else {
    return status
  }
}
